import * as classnames from "classnames";
import * as React from "react";

import {
  PageSubTitle,
  Form,
  Input,
  Button,
  Loading,
  QuickHelp,
  Msg,
  Icon,
  Radio,
} from "@shared/components";
import { ApplicantSharingExamModel } from "@shared/models";
import {
  dayjs,
  formatDateTimeMinutes,
  getTimeZoneString,
  formatTimeLeftString,
} from "@shared/services/date";
import { ExamType } from "@shared/services/enums";
import MSG from "@shared/services/message";

import Error from "../../common/error/error/Error";

/**
 * Prop interface
 */
type ExamEntranceForm = {
  email: string;
  language?: string;
};

export interface ExamEntranceProps {
  pageLanguage: string;
  error: boolean;
  loading: boolean;
  initial: ApplicantSharingExamModel;
  sendInvitation: (payload: {}) => void;
}

/**
 * State interface
 */
export interface ExamEntranceState {
  formValues: ExamEntranceForm;
  formValid: boolean;
}

/**
 * Page component
 */
class ExamEntrance extends React.Component<
  ExamEntranceProps,
  ExamEntranceState
> {
  constructor(props: ExamEntranceProps) {
    super(props);

    this.state = {
      formValues: { email: "" },
      formValid: false,
    };
  }

  public render() {
    if (this.props.error) {
      return (
        <Error
          hasBackButton={false}
          location={{
            state: {
              error: MSG.getMessageByKey("message.applicant.examNotFound"),
            },
          }}
        />
      );
    }

    if (this.props.initial.isEmpty()) {
      return <Loading isOpen={true} />;
    }

    const rootStyle = classnames("code-exam-entrance");

    const { initial, loading, pageLanguage } = this.props;

    const multiLangage = initial.examType === ExamType.EnglishJapanese;

    const showTimeLimit =
      initial.urlSharingConfig.dynamicDeadlineHours &&
      dayjs()
        .add(initial.urlSharingConfig.dynamicDeadlineHours, "hours")
        .isBefore(dayjs(initial.urlSharingConfig.endAt));

    return (
      <div className={rootStyle}>
        <Loading isOpen={loading} />
        <p className="code-exam-entrance__title">
          <Msg id="editor.entrance.title" />
        </p>
        <div className="code-exam-entrance__exam">
          <div className="code-exam-entrance__org">
            <h2 className="code-exam-entrance__orgname">
              <Msg id="common.exam" />
            </h2>
            <p>{initial.getExamName(pageLanguage)}</p>
          </div>
          <div className="code-exam-entrance__deadline">
            <dl>
              <dt>
                <Msg id="delivery.deadline" />
              </dt>
              <dd>
                {formatDateTimeMinutes(initial.urlSharingConfig.endAt)}
                <br />
                <small>({getTimeZoneString()})</small>
              </dd>
              {(showTimeLimit && (
                <dt>
                  <Msg id="timeLimit" />：
                  <QuickHelp text={MSG.getMessageByKey("timeLimitTooltip")} />
                </dt>
              )) ||
                null}
              {(showTimeLimit && (
                <dd>
                  {formatTimeLeftString(
                    initial.urlSharingConfig.dynamicDeadlineHours * 3600,
                  )}
                </dd>
              )) ||
                null}
            </dl>
          </div>
        </div>

        {initial.isAvailable() && (
          <div className="code-exam-entrance__steps">
            <PageSubTitle>
              <Msg id="editor.takeExam.steps" />
            </PageSubTitle>
            <ul className="steps has-content-centered">
              <li className="steps-segment is-active is-dashed">
                <span className="steps-marker">1</span>
                <div className="steps-content">
                  <PageSubTitle>
                    <Msg id="editor.urlDeliverySteps.deliverEmail" />
                  </PageSubTitle>
                </div>
              </li>
              <li className="steps-segment is-dashed">
                <span className="steps-marker">2</span>
                <div className="steps-content">
                  <PageSubTitle>
                    <Msg id="editor.urlDeliverySteps.receiveEmail" />
                  </PageSubTitle>
                </div>
              </li>
              <li className="steps-segment is-dashed">
                <span className="steps-marker">3</span>
                <div className="steps-content">
                  <PageSubTitle>
                    <Msg id="editor.urlDeliverySteps.accessToExam" />
                  </PageSubTitle>
                </div>
              </li>
            </ul>
          </div>
        )}

        {initial.isAvailable() ? (
          <Form
            className="code-exam-entrance__form"
            validation={{
              email: ["string", "required", "strictEmail"],
              ...(multiLangage ? { language: ["string", "required"] } : {}),
            }}
            onFormChange={this.onFormChange}
          >
            <div className="code-exam-entrance__form-container">
              {multiLangage && (
                <div className="code-exam-entrance__form-multi-lang">
                  <p>
                    <Msg id="editor.urlDeliverySteps.language.navigate" />
                  </p>
                  <div className="code-exam-entrance__form-language">
                    <Radio name="language" defaultValue="en" className="">
                      <span>English</span>
                    </Radio>
                    <Radio name="language" defaultValue="ja">
                      <span>日本語</span>
                    </Radio>
                  </div>
                </div>
              )}
              <div className="code-exam-entrance__form-email">
                <p>
                  <Msg id="editor.urlDeliverySteps.inputEmail.navigate" />
                </p>
                <Input name="email" autocomplete="email" />
                <div className="code-exam-entrance__form-email-note">
                  <Icon type="exclamation-triangle" size="medium" />
                  <Msg id="editor.urlDeliverySteps.deliverEmail.note" />
                </div>
              </div>
            </div>
            <div className="code-exam-entrance__form-button">
              <Button
                type="primary"
                onClick={this.onSendInvitation}
                disabled={!this.state.formValid || loading}
                ariaLabel="Deliver Exam"
              >
                <Msg id="action.deliverToSelf" />
              </Button>
            </div>
          </Form>
        ) : (
          <div className="code-exam-entrance__expired">
            <Msg id={"editor.deadlineExpired"} />
          </div>
        )}
      </div>
    );
  }

  private onSendInvitation = () => {
    const {
      initial: { examType },
    } = this.props;
    const { email, language } = this.state.formValues;

    this.props.sendInvitation({
      email,
      ...(examType === ExamType.EnglishJapanese ? { language } : {}),
    });
  };

  private onFormChange = (formValid: boolean, formValues: ExamEntranceForm) => {
    this.setState({ formValid, formValues });
  };
}

export default ExamEntrance;
